import {
  AssessmentOutlined,
  BuildCircleOutlined,
  BuildOutlined,
  CalculateOutlined,
  DescriptionOutlined,
  ExpandLess,
  ExpandMore,
  GavelOutlined,
  PlumbingOutlined,
  RequestQuoteOutlined,
  SettingsInputComponentOutlined,
  ShieldOutlined,
  South,
  StraightenOutlined,
  TerrainOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DonutLargeOutlinedIcon from "@mui/icons-material/DonutLargeOutlined";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import MapIcon from "@mui/icons-material/Map";
import MenuIcon from "@mui/icons-material/Menu";
import WaterOutlined from "@mui/icons-material/WaterOutlined";

import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";

import { setTab } from "../../store/UI";
import { UIStore } from "../../types/store";

import { getCaptureLocationMap } from "../../api/captures/captureLocation";
import CaptureLocationMap from "../../components/Captures/CaptureLocationMap";
import { useCapture } from "../../hooks/Capture/useCapture";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { FeatureCollection } from "../../types/model/captureLocation";

import { deleteCapture } from "../../api/captures/capture";
import CalculationsTab from "../../components/Captures/Tabs/CalculationsTab";
import CasingProfileTab from "../../components/Captures/Tabs/CasingProfileTab";
import ConstructionProfileTab from "../../components/Captures/Tabs/ConstructionProfileTab";
import ControlEquipmentTab from "../../components/Captures/Tabs/ControlEquipmentTab";
import DepthMeasurementTab from "../../components/Captures/Tabs/DepthMeasurementTab";
import DL38299Tab from "../../components/Captures/Tabs/DL38299Tab";
import ExtractionEquipmentTab from "../../components/Captures/Tabs/ExtractionEquipmentTab";
import FilesTab from "../../components/Captures/Tabs/FilesTab";
import FlowTestTab from "../../components/Captures/Tabs/FlowTestTab";
import GeneralTab from "../../components/Captures/Tabs/GeneralTab";
import InterventionTab from "../../components/Captures/Tabs/InterventionTab";
import LicensingTab from "../../components/Captures/Tabs/LicensingTab";
import LithologyTab from "../../components/Captures/Tabs/LithologyTab";
import MonitoringTab from "../../components/Captures/Tabs/MonitoringTab";
import ProtectionZoneTab from "../../components/Captures/Tabs/ProtectionZoneTab";
import PumpColumnTab from "../../components/Captures/Tabs/PumpColumnTab";
import { usePermissions } from "../../hooks";
import { useAccountPermissions } from "../../hooks/Permissions/useAccountPermissions";
import { useEntityAccessPermissions } from "../../hooks/Permissions/useEntityAccessPermissions";

export default function DetailsCapture() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const location = useLocation();
  const { hasPermission } = usePermissions();

  const { captureId } = useParams<{ captureId: string }>();

  const tabState = useSelector((state: { ui: UIStore }) => state.ui.tabState);
  const [tabIndex, setTabIndex] = useState(tabState[location.pathname] || 0);
  const [subTabIndex, setSubTabIndex] = useState<number | null>(null);

  const { captureData } = useCapture(captureId);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { canEditData } = useAccountPermissions();
  const {
    canEditEntity,
    isLoading: isLoadingEntityPermissions,
  } = useEntityAccessPermissions();
  const canEdit =
    canEditData &&
    !isLoadingEntityPermissions &&
    captureData?.entityId !== undefined &&
    canEditEntity(captureData.entityId);

  const [open, setOpen] = useState(false);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [geojson, setGeojson] = React.useState<FeatureCollection | undefined>();
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (tabIndex === 12 && subTabIndex === null) {
      setSubTabIndex(0);
    }
  }, [tabIndex, subTabIndex]);

  const toggleGroup = (index: number) => {
    setOpenGroups((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleDrawerToggle = () => {
    setIsTransitioning(true);
    setOpen(!open);
  };

  const handleTransitionEnd = () => {
    setIsTransitioning(false);
  };

  const handleTabChange = (_event: React.SyntheticEvent | React.MouseEvent, newValue: number) => {
    setTabIndex(newValue);
    dispatch(setTab({ page: location.pathname, tab: newValue }));
  };

  const fetchMapData = async () => {
    if (!captureId) return;
    try {
      const response = await getCaptureLocationMap(undefined, undefined, Number.parseInt(captureId));
      setGeojson(response);
    } catch (error) {
      handleError(error);
    }
  };

  const toggleOpenMap = () => {
    setIsMapOpen((prev) => {
      if (!prev && !geojson) {
        fetchMapData();
      }
      return !prev;
    });
  };

  const handleOpenDelete = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    if (!isDeleting) {
      setIsDeleteDialogOpen(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!captureId) {
      return;
    }

    setIsDeleting(true);

    try {
      await deleteCapture(Number(captureId));
      navigate(-1);
    } catch (error) {
      handleError(error);
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  const drawerItems = [
    { 
      text: t("captures.tabs.general"),
      icon: <InfoOutlined />,
      index: 0
    },
    { 
      text: t("captures.tabs.lithological-profile"),
      icon: <TerrainOutlined />,
      index: 1,
      permission: "CAPTURE_TAB_LITHOLOGY"
    },
    { 
      text: t("captures.tabs.depth-measurement-records"),
      icon: <South />,
      index: 2,
      permission: "CAPTURE_TAB_DEPTH_MEASUREMENT"
    },
    { 
      text: t("captures.tabs.flow-tests"),
      icon: <WaterOutlined />,
      index: 3,
      permission: "CAPTURE_TAB_FLOW_TESTS"
    },
    { 
      text: t("captures.tabs.construction-profiles"),
      icon: <StraightenOutlined />,
      index: 4,
      permission: "CAPTURE_TAB_CONSTRUCTION_PROFILE"
    },
    { 
      text: t("captures.tabs.casing-profiles"),
      icon: <DonutLargeOutlinedIcon />,
      index: 5,
      permission: "CAPTURE_TAB_CASING_PROFILE"
    },
    { 
      text: t("captures.tabs.extraction-equipments"),
      icon: <BuildOutlined />,
      index: 6,
      permission: "CAPTURE_TAB_EXTRACTION_EQUIPMENT"
    },
    { 
      text: t("captures.tabs.pump-columns"),
      icon: <PlumbingOutlined />,
      index: 7,
      permission: "CAPTURE_TAB_PUMP_COLUMN"
    },
    { 
      text: t("captures.tabs.control-equipments"),
      icon: <SettingsInputComponentOutlined />,
      index: 8,
      permission: "CAPTURE_TAB_CONTROL_EQUIPMENT"
    },
    { 
      text: t("captures.tabs.monitoring-records"),
      icon: <AssessmentOutlined />,
      index: 9,
      permission: "CAPTURE_TAB_MONITORING"
    },
    { 
      text: t("captures.tabs.interventions"),
      icon: <BuildCircleOutlined />,
      index: 10,
      permission: "CAPTURE_TAB_INTERVENTION"
    },
    { 
      text: t("captures.tabs.licensing"),
      icon: <DescriptionOutlined />,
      index: 11,
      permission: "CAPTURE_TAB_LICENSING"
    },
    {
      text: t("captures.tabs.calculations"),
      icon: <CalculateOutlined />,
      index: 12,
      permission: "CAPTURE_TAB_CALCULATIONS",
      children: [
        { 
          text: t("captures.tabs.protection-perimeters"),
          icon: <ShieldOutlined />,
          subIndex: 0
        },
        { 
          text: t("captures.tabs.vulnerability"),
          icon: <WarningAmberOutlined />,
          subIndex: 1
        },
      ],
    },
    { 
      text: t("captures.tabs.dl38299"),
      icon: <GavelOutlined />,
      index: 13,
      permission: "CAPTURE_TAB_DL38299"
    },
    { 
      text: t("captures.tabs.protection-zone"),
      icon: <RequestQuoteOutlined />,
      index: 14,
      permission: "CAPTURE_TAB_PROTECTION_ZONE"
    },
    { 
      text: t("captures.tabs.files"),
      icon: <AttachFileOutlined />,
      index: 15,
      permission: "CAPTURE_TAB_FILES"
    },
  ].filter(item => hasPermission(item.permission));

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Toolbar>
        <IconButton
          color="inherit"
          onClick={handleDrawerToggle}
          edge="start"
          sx={{ mr: 2 }}
        >
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography variant="h4">
            {t("captures.label")}
            {captureData ? ` - ${captureData.name || captureData.id}` : ""}
          </Typography>

          <Tooltip title={t("generic.open")}>
            <IconButton onClick={toggleOpenMap} disabled={isMapOpen && !geojson} sx={{ ml: 2 }}>
              <MapIcon color={isMapOpen ? "primary" : "secondary"} />
            </IconButton>
          </Tooltip>

          {canEdit && (
            <Tooltip title={t("captures.capture.delete")}>
              <IconButton color="error" onClick={handleOpenDelete}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Tooltip title={t("generic.back")}>
          <Button
            onClick={() => navigate(-1)}
            variant="contained"
            sx={{ borderRadius: 2 }}
          >
            <ArrowBackIcon />
          </Button>
        </Tooltip>
      </Toolbar>

      <Box sx={{ display: "flex" }}>
        <Drawer
          variant="permanent"
          open={open}
          sx={{
            width: open ? 240 : (theme) => `calc(${theme.spacing(7)} + 1px)`,
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
            }),
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              position: 'relative',
              whiteSpace: 'nowrap',
              boxSizing: 'border-box',
              width: 'inherit',
              overflowX: 'hidden',
              border: 'none',
              zIndex: 'auto',
            },
          }}
          onTransitionEnd={handleTransitionEnd}
        >
          <List component="nav">
            {drawerItems.map((item) => (
              <Box key={item.index}>
                <ListItem disablePadding>
                  <Tooltip title={open ? "" : item.text} placement="left">
                    <ListItemButton
                      selected={tabIndex === item.index}
                      onClick={(e) => {
                        if (item.children) {
                          toggleGroup(item.index);
                        } else {
                          handleTabChange(e, item.index);
                          setSubTabIndex(null);
                        }
                      }}
                      sx={{ minHeight: 48, justifyContent: 'initial', px: 2.5, alignItems: 'center' }}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: 'center' }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        sx={{
                          opacity: open ? 1 : 0,
                          transition: (theme) => theme.transitions.create('opacity'),
                          whiteSpace: open && !isTransitioning ? 'normal' : 'nowrap',
                          overflow: 'hidden',
                        }}
                      />
                      {item.children && (openGroups[item.index] ? <ExpandLess /> : <ExpandMore />)}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>

                {item.children && (
                  <Collapse in={openGroups[item.index]} timeout="auto" unmountOnExit>
                    {item.children
                      .map((child) => (
                      <ListItem disablePadding key={child.subIndex}>
                        <Tooltip key={child.subIndex} title={open ? "" : child.text} placement="left">
                          <ListItemButton
                            sx={{
                              pl: 2.5,
                              pr: 2.5,
                              justifyContent: 'initial',
                            }}
                            selected={tabIndex === item.index && subTabIndex === child.subIndex}
                            onClick={(e) => {
                              handleTabChange(e, item.index);
                              setSubTabIndex(child.subIndex);
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: 'center' }}>
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.text}
                              sx={{
                                opacity: open ? 1 : 0,
                                transition: (theme) => theme.transitions.create('opacity'),
                                whiteSpace: open && !isTransitioning ? 'normal' : 'nowrap',
                                overflow: 'hidden',
                              }}
                            />
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    ))}
                  </Collapse>
                )}
              </Box>
            ))}
          </List>
        </Drawer>

        <Box sx={{ flexGrow: 1, p: 3, display: "grid", gridTemplateColumns: "1fr" }}>
          <Fade in={tabIndex === 0} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
            <Box sx={{ gridColumn: 1, gridRow: 1 }}>
              {captureId && captureData && <GeneralTab captureId={captureId} handleError={handleError} canEdit={canEdit} />}
            </Box>
          </Fade>

          {hasPermission("CAPTURE_TAB_LITHOLOGY") && (
            <Fade in={tabIndex === 1} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <LithologyTab captureId={captureId} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_DEPTH_MEASUREMENT") && (
            <Fade in={tabIndex === 2} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <DepthMeasurementTab captureId={captureId} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_FLOW_TESTS") && (
            <Fade in={tabIndex === 3} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <FlowTestTab captureId={captureId} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_CONSTRUCTION_PROFILE") && (
            <Fade in={tabIndex === 4} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <ConstructionProfileTab captureId={captureId} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_CASING_PROFILE") && (
            <Fade in={tabIndex === 5} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <CasingProfileTab captureId={captureId} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_EXTRACTION_EQUIPMENT") && (
            <Fade in={tabIndex === 6} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <ExtractionEquipmentTab captureId={captureId} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_PUMP_COLUMN") && (
            <Fade in={tabIndex === 7} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <PumpColumnTab captureId={captureId} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_CONTROL_EQUIPMENT") && (
            <Fade in={tabIndex === 8} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <ControlEquipmentTab captureId={captureId} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_MONITORING") && (
            <Fade in={tabIndex === 9} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <MonitoringTab captureId={captureId} captureName={captureData.name} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_INTERVENTION") && (
            <Fade in={tabIndex === 10} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <InterventionTab captureId={captureId} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_LICENSING") && (
            <Fade in={tabIndex === 11} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <LicensingTab captureId={captureId} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_CALCULATIONS") && (
            <Fade in={tabIndex === 12} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <CalculationsTab captureId={captureId} subIndex={subTabIndex ?? 0} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_DL38299") && (
            <Fade in={tabIndex === 13} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <DL38299Tab captureId={captureId} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_PROTECTION_ZONE") && (
            <Fade in={tabIndex === 14} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <ProtectionZoneTab captureId={captureId} handleError={handleError} canEdit={canEdit} />}
              </Box>
            </Fade>
          )}

          {hasPermission("CAPTURE_TAB_FILES") && (
            <Fade in={tabIndex === 15} timeout={{ enter: 200, exit: 0 }} unmountOnExit>
              <Box sx={{ gridColumn: 1, gridRow: 1 }}>
                {captureId && captureData && <FilesTab captureId={Number(captureId)} entityId={captureData.entityId} />}
              </Box>
            </Fade>
          )}
        </Box>
      </Box>

      <Dialog
        open={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isFullScreen}
        PaperProps={{
          sx: {
            height: isFullScreen ? "100%" : "lg"
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>
            {t("captures.map.title")}
          </Typography>

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title={isFullScreen ? t("generic.exit-fullscreen") : t("generic.fullscreen")}>
            <IconButton onClick={() => setIsFullScreen((p) => !p)}>
              {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>

          <Tooltip title={t("generic.close")}>
            <IconButton onClick={() => setIsMapOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent dividers>
          <CaptureLocationMap geojson={geojson} selectedCaptures={captureId ? [Number(captureId)] : []} isMaximized={isFullScreen} captureId={captureId ? Number(captureId) : undefined}/>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle>
          {t("captures.capture.delete-confirmation-title")}
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            {t("captures.capture.delete-confirmation-message")}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleCancelDelete}
            disabled={isDeleting}
          >
            {t("generic.cancel")}
          </Button>

          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={
              isDeleting
                ? <CircularProgress size={16} color="inherit" />
                : undefined
            }
          >
            {t("generic.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
